<template>
    <Dialog
      title="Mark as Reorder"
      :showCloseButton="true"
      :showFooter="true"
      :showSaveButton="true"
      :showCancelButton="false"
      saveButtonText="Mark as reorder"
      cancelButtonText="Cancel"
      :sm="true"
      @closeEvent="closeEvent"
      @cancelEvent="closeEvent"
      @save-event="markAsReorder"
    >
      <template #modal-content>
        <p class="mb-2">Select Order:</p>
        <Select
          v-model="selectedOrder"
          append-to-body
          id="select_order"
          name="select_order"
          placeholder="Search orders by reference number..."
          label="customer_reference_no"
          track-by="id"
          :options="orders"
          :loading="isLoadingOrders"
          :searchable="true"
          @option:selected="(order) => getOrderItemsForReorders(order.id)"
        />
        <template v-if="orderItems.length > 0">
          <p class="mb-2">Select Factory:</p>
          <Select
            v-model="selectedOrderItem"
            append-to-body
            id="select_order"
            name="select_order"
            placeholder="Select factory..."
            track-by="id"
            :options="orderItems"
            :loading="isLoadingOrders"
            :get-option-label="getOrderItemLabel"
            @option:selected="(orderItem) => getFactoryProductsForOrderItem(orderItem.id)"
          />
        </template>
        <template v-if="factoryProducts.length > 0">
          <p class="mb-2">Select Product:</p>
          <Select
            v-model="selectedFactoryProduct"
            append-to-body
            id="select_order"
            name="select_order"
            placeholder="Select product..."
            track-by="id"
            :get-option-label="getFactoryProductLabel"
            :options="factoryProducts"
            :loading="isLoadingOrders"
          />
        </template>
        <div class="flex justify-between flex-col mt-4 gap-2">
          <label class="inline-flex items-center">
            <input
              type="checkbox"
              v-model="design_change"
              class="text-indigo-600 border-line rounded shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
            />
            <span class="ml-2">Design change</span>
          </label>
          <label class="inline-flex items-center">
            <input
              type="checkbox"
              v-model="roster_change"
              class="text-indigo-600 border-line rounded shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
            />
            <span class="ml-2">Roster change</span>
          </label>
        </div>

        <!--
          AB#20682 AC5: until now an incorrect reorder link could not be undone
          in any screen, so an order wrongly tagged as a reorder stayed that
          way. This clears the tag and the order displays as a new order.
        -->
        <div class="mt-4 pt-3 border-t border-line">
          <button
            type="button"
            class="text-sm text-red-600 hover:text-red-700 disabled:opacity-50"
            :disabled="isRemovingTag"
            data-test="remove-reorder-tag"
            @click="removeReorderTag"
          >
            {{ isRemovingTag ? 'Removing…' : 'Not a reorder — remove reorder tag' }}
          </button>
          <p class="mt-1 text-xs text-ink-mute">
            Clears the reorder reference on this item and shows it as a new order.
          </p>
        </div>
      </template>
    </Dialog>
  </template>
  <script lang="ts" setup>
  import Dialog from '@/components/Shared/Dialog.vue';
import Select from '@/components/Shared/Select.vue';
import { showToast } from '@/composables/swal';
import { OrderItem } from '@/composables/useOrderDetail';
import { handleResponseException, http } from '@/interceptors/axiosRequestInterceptor';
import { FactoryProduct, Order } from '@/models';
import { Option } from 'lucide-vue-next';
import { onMounted, ref } from 'vue';
  const props = defineProps<{
    order_item_id: any;
    product_id: any;
    factory_product_id: any;
    order_no: any;
  }>();
  const markAsReorderModal = ref<boolean>(false);
  const orders = ref<Order[]>([]);
  const allOrders = ref<Order[]>([]); // Store original unfiltered orders
  const orderItems = ref<OrderItem[]>([]);
  const selectedOrder = ref<Order | null>(null);
  const selectedOrderItem = ref<OrderItem | null>(null);
  const isLoadingOrders = ref<boolean>(false);
  const searchQuery = ref<string>('');
  const factoryProducts = ref<FactoryProduct[]>([]);
  const selectedFactoryProduct = ref<FactoryProduct | null>(null);
  const design_change = ref<boolean>(false);
  const roster_change = ref<boolean>(false);
  const isRemovingTag = ref<boolean>(false);

  const emit = defineEmits(['closeEvent', 'handleOrderUpdated']);


  const closeEvent = () => {
    emit('closeEvent');
    markAsReorderModal.value = false;
  };

  // Method to get order for reorders
  const getOrderForReorders = async () => {
    try {
      isLoadingOrders.value = true;
      // AB#20682: order_item_id lets the backend scope candidates to the same
      // customer and company as the order being edited. Without it the picker
      // listed every order containing this product, across all merchants.
      const url = `get-order-for-reorders?product_id=${props.product_id}&order_item_id=${props.order_item_id}`;

      const response = await http.get(url);
      allOrders.value = response.data.result.orders.filter((order: Order) => order.order_no ? order.order_no !== props.order_no : order);
      orders.value = response.data.result.orders.filter((order: Order) => order.order_no ? order.order_no !== props.order_no : order);
      return response.data;
    } catch (error) {
      handleResponseException(error);
      throw error;
    } finally {
      isLoadingOrders.value = false;
    }
  };

  const getOrderItemsForReorders = async (order_id: number) => {
    try {
      isLoadingOrders.value = true;
      let url = `get-order-items-for-order?order_id=${order_id}`;
      const response = await http.get(url)
      orderItems.value = response.data.result.order_items;
      if (orderItems.value.length === 1) {
        selectedOrderItem.value = orderItems.value[0];
        getFactoryProductsForOrderItem(selectedOrderItem.value?.id);
      }
    } catch (error) {
      handleResponseException(error);
      throw error;
    } finally {
      isLoadingOrders.value = false;
    }
  }

  const getFactoryProductsForOrderItem = async (order_item_id: number) => {
    try {
      isLoadingOrders.value = true;
      let url = `get-factory-products-for-order-item?order_item_id=${order_item_id}`;
      const response = await http.get(url)
      factoryProducts.value = response.data.result.factory_products;
      if (factoryProducts.value.length === 1) {
        selectedFactoryProduct.value = factoryProducts.value[0];
      }
    } catch (error) {
      handleResponseException(error);
      throw error;
    } finally {
      isLoadingOrders.value = false;
    }
  }

  const markAsReorder = async () => {
    if (!selectedFactoryProduct.value) {
      showToast('Please select all values', 'error');
      return;
    }
    try {
      let url = `mark-as-manual-reorder`;
      let payload = {
        order_item_id: props.order_item_id,
        factory_product_id: props.factory_product_id,
        product_id: props.product_id,
        reorder_order_item_id: selectedOrderItem.value?.id,
        reorder_factory_product_id: selectedFactoryProduct.value?.id,
        design_change: design_change.value,
        roster_change: roster_change.value,
        reorder_order_no: selectedOrder.value?.order_no,
      }
      const response = await http.post(url, payload);
      showToast(response.data.message, 'success');
      closeEvent();
      emit('handleOrderUpdated');
    } catch (err) {
      handleResponseException(err);
    }
  }

  /**
   * AB#20682 AC5 — clear an incorrect reorder link so the order shows as a
   * new order. Nothing previously existed to undo a wrong reference.
   */
  const removeReorderTag = async () => {
    try {
      isRemovingTag.value = true;
      const response = await http.post('remove-reorder-tag', {
        order_item_id: props.order_item_id,
        factory_product_id: props.factory_product_id,
      });
      showToast(response.data.message, 'success');
      closeEvent();
      emit('handleOrderUpdated');
    } catch (err) {
      handleResponseException(err);
    } finally {
      isRemovingTag.value = false;
    }
  }

  const getFactoryProductLabel = (option) => {
    if (!option) return "Name not available";

    return option.product_name
      ? option.product_name
      : option.new_product_name
        ? option.new_product_name
        : "Name not available";
  }

  const getOrderItemLabel = (option) => {
    if (!option) return "Name not available";

    return option.factory_name
      ? option.factory_name
      : "Shareable";
  }

  onMounted(() => {
    getOrderForReorders();
  })
  </script>
  <style scoped lang="scss"></style>
